import type { FC } from 'react';
import { useState } from 'react';
import {
  Divider,
  ProductThumbnail,
  useBreakpoint
} from '@cencosud-cencommerce/paris-uikit';
import classNames from 'classnames';

import { useCart } from '@/Application/Selectors/cart/useCart';
import { LoadingCircle } from '@/modules/perseo-billing/ui/react/src';
import { SendShippingEvents } from '@/modules/shared/utils/gtmEvents';
import { sendEvent } from '@/modules/shared/utils/gtmEvents/sendEvents/sendShippingEvents';
import {
  ShippingEventInteractiveActions,
  ShippingEventInteractiveTags
} from '@/modules/shared/utils/gtmEvents/types';

import useAddToSaveForLater from '../../hooks/mutators/useAddSaveForLater';

import NoProductsOnStock from './NoProductsOnStock';
import ProductItem from './ProductItem';
import ToggleProducts from './ToggleProducts';

const ProductList: FC = () => {
  const { data: cart } = useCart();
  const products = cart?.outOfStockItems ?? [];
  const { isMobile } = useBreakpoint();
  const isUniqueProduct = products.length === 1;
  const [isOpen, setIsOpen] = useState(isUniqueProduct);
  const firstsElements = products.slice(0, 2);
  const restElements = products.slice(2);
  const showCollapsedList = isMobile && !isOpen && !isUniqueProduct;
  const { trigger: saveForLater, isLoading } = useAddToSaveForLater();
  const isRemovingLastElement = products.length === 1 && isLoading;
  const allProductsAreOutOfStock = cart?.lineItems.length === 0;

  if (!products.length) return null;

  return (
    <div>
      <div
        className={classNames({
          'co-flex co-justify-between co-items-center': showCollapsedList
        })}
      >
        {isRemovingLastElement && (
          <div className="co-absolute co-top-0 co-left-0 co-w-full co-h-full co-bg-white co-bg-opacity-85 co-z-10 co-flex co-items-center co-justify-center">
            <LoadingCircle width={32} height={32} />
          </div>
        )}
        {showCollapsedList && (
          <div className="co-flex co-gap-[4px]">
            {firstsElements.map((product) => (
              <ProductThumbnail
                variant="image-counter"
                key={product.id}
                src={product.image}
              />
            ))}
            {restElements.length > 0 && (
              <ProductThumbnail
                variant="counter"
                counterLabel={`+${String(restElements.length)}`}
              />
            )}
          </div>
        )}
        {isMobile && !isUniqueProduct && (
          <div className="co-flex co-justify-end co-items-center co-h-[48px] co-my-[8px]">
            <ToggleProducts
              isOpen={isOpen}
              toggleOpen={() => {
                setIsOpen(!isOpen);
                if (!isOpen) {
                  SendShippingEvents.sendEvent({
                    event: 'baseEvent',
                    extraFields: {
                      'event-interactive-accion':
                        ShippingEventInteractiveActions.OutOfStock,
                      'event-interactive-tag':
                        ShippingEventInteractiveTags.ShowProducts
                    }
                  });
                }
              }}
            />
          </div>
        )}
      </div>
      {allProductsAreOutOfStock && !isMobile && <NoProductsOnStock />}
      {!showCollapsedList &&
        products.map((product, index) => (
          <div className="co-relative" key={product.id}>
            {isLoading && !isRemovingLastElement && (
              <div className="co-absolute co-w-full co-h-full co-bg-white co-bg-opacity-85 co-z-10 co-flex co-items-center co-justify-center">
                <LoadingCircle width={32} height={32} />
              </div>
            )}
            <ProductItem
              key={index}
              product={product}
              onSaveClick={() => {
                sendEvent({
                  event: 'baseEvent',
                  extraFields: {
                    'event-interactive-accion':
                      ShippingEventInteractiveActions.OutOfStock,
                    'event-interactive-tag':
                      ShippingEventInteractiveTags.SaveForLater
                  }
                });
                saveForLater({ sku: product.sku, lineItemId: product.id });
              }}
              isAnonymousUser={!!cart?.anonymousId}
            />
            {index < products.length - 1 && <Divider />}
          </div>
        ))}
    </div>
  );
};

export default ProductList;
